/**
 * PAGE REGISTRY - Source of Truth Frontend
 * =========================================
 * 
 * Ce module expose le Page Registry comme source de vérité unique
 * pour la gestion des pages dans le frontend.
 * 
 * RÈGLES:
 * - Le registry est READONLY
 * - CMS ne peut JAMAIS modifier le registry
 * - Toute validation de route passe par ce module
 */

// Types
export const PAGE_TYPES = {
  HUB: 'HUB',
  LISTING: 'LISTING',
  CMS_CONTENT: 'CMS_CONTENT',
  ADMIN: 'ADMIN',
  PARTNER: 'PARTNER',
  FUNCTIONAL: 'FUNCTIONAL',
  AUTH: 'AUTH',
  PUBLIC: 'PUBLIC'
};

// ============================================================================
// REGISTRY DATA (synchronisé avec PAGE_REGISTRY.json)
// ============================================================================

const REGISTRY = {
  // HUB Pages
  'nouveautes': {
    route: '/dashboard/nouveautes',
    type: PAGE_TYPES.HUB,
    pageConfig: 'nouveautes',
    children: ['/dashboard/nouveautes/en-cours', '/dashboard/nouveautes/a-venir']
  },
  'precommandes': {
    route: '/dashboard/precommandes',
    type: PAGE_TYPES.HUB,
    pageConfig: 'precommandes',
    children: ['/dashboard/precommandes/en-cours', '/dashboard/precommandes/a-venir']
  },
  'offres': {
    route: '/dashboard/offres',
    type: PAGE_TYPES.HUB,
    pageConfig: 'offres',
    children: ['/dashboard/offres/en-cours']
  },
  
  // LISTING Pages
  'nouveautes-en-cours': {
    route: '/dashboard/nouveautes/en-cours',
    type: PAGE_TYPES.LISTING,
    pageConfig: 'nouveautes-en-cours',
    parent: 'nouveautes'
  },
  'nouveautes-a-venir': {
    route: '/dashboard/nouveautes/a-venir',
    type: PAGE_TYPES.LISTING,
    pageConfig: 'nouveautes-a-venir',
    parent: 'nouveautes'
  },
  'precommandes-en-cours': {
    route: '/dashboard/precommandes/en-cours',
    type: PAGE_TYPES.LISTING,
    pageConfig: 'precommandes-en-cours',
    parent: 'precommandes'
  },
  'precommandes-a-venir': {
    route: '/dashboard/precommandes/a-venir',
    type: PAGE_TYPES.LISTING,
    pageConfig: 'precommandes-a-venir',
    parent: 'precommandes'
  },
  'offres-en-cours': {
    route: '/dashboard/offres/en-cours',
    type: PAGE_TYPES.LISTING,
    pageConfig: 'offres-en-cours',
    parent: 'offres'
  },
  
  // FUNCTIONAL Pages
  'dashboard': { route: '/dashboard', type: PAGE_TYPES.FUNCTIONAL },
  'favoris': { route: '/dashboard/favoris', type: PAGE_TYPES.FUNCTIONAL },
  'profile': { route: '/dashboard/profil', type: PAGE_TYPES.FUNCTIONAL },
  'personal-space': { route: '/dashboard/personal-space', type: PAGE_TYPES.FUNCTIONAL },
  'search': { route: '/search', type: PAGE_TYPES.FUNCTIONAL },
  
  // ADMIN Pages
  'admin-dashboard': { route: '/dashboard/admin', type: PAGE_TYPES.ADMIN },
  'admin-produits': { route: '/dashboard/admin/produits', type: PAGE_TYPES.ADMIN },
  'admin-partenaires': { route: '/dashboard/admin/partenaires', type: PAGE_TYPES.ADMIN },
  'admin-pages': { route: '/dashboard/admin/pages', type: PAGE_TYPES.ADMIN },
  'admin-utilisateurs': { route: '/dashboard/admin/utilisateurs', type: PAGE_TYPES.ADMIN },
  'admin-publications': { route: '/dashboard/admin/publications', type: PAGE_TYPES.ADMIN },
  'admin-avis': { route: '/dashboard/admin/avis', type: PAGE_TYPES.ADMIN },
  'admin-global-blocks': { route: '/dashboard/admin/global-blocks', type: PAGE_TYPES.ADMIN },
  
  // PARTNER Pages
  'partner-dashboard': { route: '/dashboard/partner', type: PAGE_TYPES.PARTNER },
  'partner-produits': { route: '/dashboard/partner/produits', type: PAGE_TYPES.PARTNER }
};

// ============================================================================
// PROTECTED ROUTES
// ============================================================================

const PROTECTED_ROUTES = {
  hub: [
    '/dashboard/nouveautes',
    '/dashboard/precommandes',
    '/dashboard/offres',
    '/dashboard/selection',
    '/dashboard/bestsellers',
    '/dashboard/tcg'
  ],
  listing_prefixes: [
    '/dashboard/nouveautes/',
    '/dashboard/precommandes/',
    '/dashboard/offres/',
    '/dashboard/selection/',
    '/dashboard/bestsellers/',
    '/dashboard/tcg/'
  ],
  admin: [
    '/dashboard/admin'
  ],
  partner: [
    '/dashboard/partner'
  ],
  functional: [
    '/dashboard/favoris',
    '/dashboard/produit/',
    '/dashboard/profil',
    '/dashboard/personal-space'
  ]
};

// ============================================================================
// API FUNCTIONS
// ============================================================================

/**
 * Récupère une page du registry par son ID
 */
export const getPageById = (pageId) => {
  return REGISTRY[pageId] || null;
};

/**
 * Récupère une page du registry par sa route
 */
export const getPageByRoute = (route) => {
  const normalizedRoute = route.toLowerCase().replace(/\/$/, '');
  
  for (const [pageId, page] of Object.entries(REGISTRY)) {
    if (page.route.toLowerCase() === normalizedRoute) {
      return { id: pageId, ...page };
    }
  }
  return null;
};

/**
 * Vérifie si une route est protégée
 */
export const isProtectedRoute = (route) => {
  if (!route) return { protected: false, reason: '' };
  
  const normalized = route.toLowerCase().replace(/\/$/, '');
  
  // Check exact HUB routes
  if (PROTECTED_ROUTES.hub.includes(normalized)) {
    return { protected: true, reason: 'Route HUB système', type: 'HUB' };
  }
  
  // Check LISTING prefixes
  for (const prefix of PROTECTED_ROUTES.listing_prefixes) {
    if (normalized.startsWith(prefix.toLowerCase())) {
      return { protected: true, reason: 'Route LISTING système', type: 'LISTING' };
    }
  }
  
  // Check ADMIN routes
  for (const admin of PROTECTED_ROUTES.admin) {
    if (normalized.startsWith(admin.toLowerCase())) {
      return { protected: true, reason: 'Route Admin système', type: 'ADMIN' };
    }
  }
  
  // Check PARTNER routes
  for (const partner of PROTECTED_ROUTES.partner) {
    if (normalized.startsWith(partner.toLowerCase())) {
      return { protected: true, reason: 'Route Partenaire système', type: 'PARTNER' };
    }
  }
  
  // Check FUNCTIONAL routes
  for (const func of PROTECTED_ROUTES.functional) {
    if (normalized.startsWith(func.toLowerCase())) {
      return { protected: true, reason: 'Route Fonctionnelle système', type: 'FUNCTIONAL' };
    }
  }
  
  return { protected: false, reason: '' };
};

/**
 * Vérifie si le CMS peut créer une page sur cette route
 */
export const canCmsCreatePage = (route) => {
  const { protected: isProtected } = isProtectedRoute(route);
  return !isProtected;
};

/**
 * Vérifie si le CMS peut ajouter des blocs sur cette page
 */
export const canCmsDecorate = (route) => {
  const page = getPageByRoute(route);
  if (!page) return true; // Pages CMS custom
  
  const type = page.type;
  return [PAGE_TYPES.HUB, PAGE_TYPES.LISTING, PAGE_TYPES.CMS_CONTENT, PAGE_TYPES.PUBLIC].includes(type);
};

/**
 * Retourne les types de blocs CMS autorisés pour une route
 */
import { getAllowedBlockTypes } from './cmsBlockRegistry';

/**
 * Returns allowed CMS block types for a given route (via registry).
 */
export const getAllowedCmsBlocks = (route) => {
  const page = getPageByRoute(route);
  
  if (!page) {
    // Page CMS custom - use CUSTOM page type from registry
    return getAllowedBlockTypes('CUSTOM');
  }
  
  switch (page.type) {
    case PAGE_TYPES.HUB:
      return getAllowedBlockTypes('HUB');
    case PAGE_TYPES.LISTING:
      return getAllowedBlockTypes('LISTING');
    case PAGE_TYPES.CMS_CONTENT:
    case PAGE_TYPES.PUBLIC:
      return getAllowedBlockTypes('CMS_CONTENT');
    default:
      return []; // ADMIN, PARTNER, FUNCTIONAL - pas de CMS
  }
};

/**
 * Retourne toutes les pages HUB
 */
export const getHubPages = () => {
  return Object.entries(REGISTRY)
    .filter(([, page]) => page.type === PAGE_TYPES.HUB)
    .map(([id, page]) => ({ id, ...page }));
};

/**
 * Retourne toutes les pages LISTING
 */
export const getListingPages = () => {
  return Object.entries(REGISTRY)
    .filter(([, page]) => page.type === PAGE_TYPES.LISTING)
    .map(([id, page]) => ({ id, ...page }));
};

/**
 * Retourne les enfants (LISTING) d'un HUB
 */
export const getHubChildren = (hubId) => {
  const hub = REGISTRY[hubId];
  if (!hub || hub.type !== PAGE_TYPES.HUB) return [];
  
  return (hub.children || []).map(route => getPageByRoute(route)).filter(Boolean);
};

/**
 * Retourne le parent HUB d'une page LISTING
 */
export const getListingParent = (listingId) => {
  const listing = REGISTRY[listingId];
  if (!listing || listing.type !== PAGE_TYPES.LISTING) return null;
  
  return listing.parent ? { id: listing.parent, ...REGISTRY[listing.parent] } : null;
};

// ============================================================================
// VALIDATION
// ============================================================================

/**
 * Valide une tentative de création de page CMS
 */
export const validateCmsPageCreate = (slug) => {
  const errors = [];
  const warnings = [];
  
  const { protected: isProtected, reason, type } = isProtectedRoute(slug);
  
  if (isProtected) {
    errors.push(`🔒 LOCKDOWN: Impossible de créer une page CMS sur ${slug}`);
    errors.push(`Raison: ${reason}`);
    
    if (type === 'HUB' || type === 'LISTING') {
      warnings.push('💡 Vous pouvez ajouter des blocs CMS à cette page existante, mais pas la remplacer.');
    }
  }
  
  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Valide l'ajout d'un bloc CMS sur une page
 */
export const validateCmsBlockAdd = (route, blockType) => {
  const allowedBlocks = getAllowedCmsBlocks(route);
  
  if (allowedBlocks.length === 0) {
    return {
      valid: false,
      error: '🔒 Aucun bloc CMS autorisé sur cette page système'
    };
  }
  
  if (!allowedBlocks.includes(blockType)) {
    return {
      valid: false,
      error: `🔒 Le bloc '${blockType}' n'est pas autorisé sur cette page. Blocs autorisés: ${allowedBlocks.join(', ')}`
    };
  }
  
  return { valid: true };
};

// Export par défaut
export default {
  PAGE_TYPES,
  REGISTRY,
  PROTECTED_ROUTES,
  getPageById,
  getPageByRoute,
  isProtectedRoute,
  canCmsCreatePage,
  canCmsDecorate,
  getAllowedCmsBlocks,
  getHubPages,
  getListingPages,
  getHubChildren,
  getListingParent,
  validateCmsPageCreate,
  validateCmsBlockAdd
};
